import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Sparkles,
  Brain,
  BookOpen,
  Users,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { ResearchOpportunity, ResearchField } from '../types';
import { ResearchCard } from '../components/research';
import { AIService } from '../services/aiService';
import { Button } from '../components/shared/Button';
import { Card } from '../components/shared/Card';
import { Badge } from '../components/shared/Badge';

export const Research: React.FC = () => {
  const [opportunities, setOpportunities] = useState<ResearchOpportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<ResearchOpportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedField, setSelectedField] = useState<ResearchField | 'all'>('all');
  const [selectedType, setSelectedType] = useState<'paid' | 'unpaid' | 'academic-credit' | 'all'>('all');
  const [sortBy, setSortBy] = useState<'match' | 'deadline' | 'alphabetical'>('match');

  const fields: { value: ResearchField | 'all'; label: string }[] = [
    { value: 'all', label: 'All Fields' },
    { value: 'stem', label: 'STEM' },
    { value: 'medicine', label: 'Medicine' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'computer-science', label: 'Computer Science' },
    { value: 'biology', label: 'Biology' },
    { value: 'chemistry', label: 'Chemistry' },
    { value: 'physics', label: 'Physics' },
    { value: 'psychology', label: 'Psychology' },
    { value: 'social-science', label: 'Social Science' },
    { value: 'humanities', label: 'Humanities' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'other', label: 'Other' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'academic-credit', label: 'Academic Credit' }
  ];

  const findOpportunities = async () => {
    setIsLoading(true);
    try {
      // Simulate user interests and grade level
      const interests = ['computer science', 'mathematics', 'science'];
      const gradeLevel = 8;

      const results = await AIService.findResearchOpportunities(interests, gradeLevel);
      setOpportunities(results);
    } catch (error) {
      console.error('Failed to fetch research opportunities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    findOpportunities();
  }, []);

  useEffect(() => {
    let filtered = opportunities.filter(opp => {
      const matchesSearch = opp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           opp.institution.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           opp.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesField = selectedField === 'all' || opp.field === selectedField;
      const matchesType = selectedType === 'all' || opp.type === selectedType;
      return matchesSearch && matchesField && matchesType;
    });

    // Sort results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'match':
          return b.matchScore - a.matchScore;
        case 'deadline':
          return a.deadline.getTime() - b.deadline.getTime();
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredOpportunities(filtered);
  }, [opportunities, searchTerm, selectedField, selectedType, sortBy]);

  const handleApply = (opportunity: ResearchOpportunity) => {
    window.open(opportunity.applicationLink, '_blank');
  };

  const handleSave = (opportunity: ResearchOpportunity) => {
    // In a real app, this would save to user's saved opportunities
    console.log('Saved opportunity:', opportunity.title);
  };

  const stats = {
    total: opportunities.length,
    highMatch: opportunities.filter(opp => opp.matchScore >= 90).length,
    deadlineSoon: opportunities.filter(opp => {
      const daysUntil = Math.ceil((opp.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil <= 30 && daysUntil > 0;
    }).length,
    paid: opportunities.filter(opp => opp.type === 'paid').length
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center">
          <Brain className="w-8 h-8 mr-3 text-primary-500" />
          AI Research Finder
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          Discover research opportunities perfectly matched to your interests and academic level
        </p>

        <Button
          onClick={findOpportunities}
          disabled={isLoading}
          className="flex items-center space-x-2"
        >
          <Sparkles size={20} />
          <span>{isLoading ? 'Finding Opportunities...' : 'Refresh Recommendations'}</span>
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-xl mr-4">
              <BookOpen className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Found</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-secondary-100 rounded-xl mr-4">
              <TrendingUp className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">High Match</p>
              <p className="text-2xl font-bold text-gray-900">{stats.highMatch}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-accent-100 rounded-xl mr-4">
              <Calendar className="w-6 h-6 text-accent-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Due Soon</p>
              <p className="text-2xl font-bold text-gray-900">{stats.deadlineSoon}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-xl mr-4">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Paid</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paid}</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search opportunities, institutions, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Filter Row */}
            <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
              <div className="flex items-center space-x-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value as ResearchField | 'all')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {fields.map(field => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {typeOptions.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="match">Best Match</option>
                  <option value="deadline">Deadline</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
              </div>

              <div className="ml-auto">
                <Badge variant="secondary" size="sm">
                  {filteredOpportunities.length} results
                </Badge>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Opportunities Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full"
          />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          {filteredOpportunities.map((opportunity, index) => (
            <motion.div
              key={opportunity.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * (index % 6) }}
            >
              <ResearchCard
                opportunity={opportunity}
                onApply={handleApply}
                onSave={handleSave}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {filteredOpportunities.length === 0 && !isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No opportunities found
          </h3>
          <p className="text-gray-500 mb-6">
            Try adjusting your search criteria or refresh for new recommendations
          </p>
          <Button onClick={findOpportunities}>
            Find New Opportunities
          </Button>
        </motion.div>
      )}
    </div>
  );
};